import { formatDateOnly } from "@/utils/date";
import { Body, Box, BulletList, ButtCon, Heading, Stack } from "@biom3/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Link from "next/link";
import { KeyInfo } from "./KeyInfo/KeyInfo";
import { RedeemKeyButton } from "./Redemption/RedeemKeyButton";
import { useWeeklyRewards } from "./WeeklyRewardsProvider";
import { OverallPrizeTypes } from "./tiers/TiersContent";
import { formatDateToMonthDay } from "./tiers/config";

interface RuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  gemsEntryAmount: string | number;
  hasFreeEntry: boolean;
  hasEnoughGems: boolean;
  showGetKeyButton?: boolean;
  hasTicketUpgrade: boolean;
  onRedeemTicket?: () => Promise<void>;
}

export function RuleModal({
  isOpen,
  onClose,
  gemsEntryAmount,
  hasFreeEntry,
  hasEnoughGems,
  showGetKeyButton,
  hasTicketUpgrade,
  onRedeemTicket,
}: RuleModalProps) {
  const { prizeConfig, activityPeriod, sweepstake } = useWeeklyRewards();

  const sweepstakeName =
    sweepstake?.current?.questName || sweepstake?.previous?.questName;
  const isLiveProgressKey = sweepstake?.current?.isLiveProgressKey;
  const claimDate = sweepstake?.next?.startDate
    ? `${formatDateOnly(new Date(sweepstake.next.startDate))} 1am`
    : "Thursday 1am";
  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Weekly Rewards</DialogTitle>
      <ButtCon
        icon="Close"
        variant="tertiary"
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: "base.spacing.x2",
          top: "base.spacing.x2",
        }}
      />

      <DialogContent sx={{ px: 0 }} dividers>
        <Box sx={{ p: "base.spacing.x6", pt: "0" }}>
          <Stack gap="base.spacing.x6">
            <Stack direction="row" gap="base.spacing.x4">
              <KeyInfo
                icon="ImxRewards"
                title="Gems to Redeem"
                value={`${gemsEntryAmount || prizeConfig.defaultGemEntry}`}
              />
              <KeyInfo icon="Calendar" title="Occurs" value="Every Thursday" />
              <KeyInfo icon="Key" title="Entries" value="1" />
            </Stack>

            <Stack gap="base.spacing.x1">
              <Heading size="small">Rules</Heading>
              <BulletList>
                <BulletList.Item>
                  <strong>{sweepstakeName}:</strong>{" "}
                  {formatDateToMonthDay(prizeConfig.weeklyFrom)}–
                  {formatDateToMonthDay(prizeConfig.weeklyTo)} (UTC).
                </BulletList.Item>
                <BulletList.Item>
                  <strong>Redeem:</strong> {prizeConfig.defaultGemEntry} Gems
                  for 1 Key ({isLiveProgressKey ? "Thu–Thu" : "Thu–Mon"}, UTC)
                </BulletList.Item>
                <BulletList.Item>
                  <strong>
                    {isLiveProgressKey ? "Key Tiers" : "Key Types"}:
                  </strong>{" "}
                  Common, Uncommon, Rare, Epic, Legendary
                  <BulletList>
                    <BulletList.Item>
                      {isLiveProgressKey
                        ? "Updates is real time based on how many"
                        : "Based on how many"}{" "}
                      <Link
                        href="/quests/#main-quests"
                        style={{
                          textDecoration: "none",
                        }}
                      >
                        eligible quests
                      </Link>{" "}
                      you completed in between{" "}
                      <strong>
                        {formatDateToMonthDay(activityPeriod?.from || "")}
                      </strong>
                      –
                      <strong>
                        {formatDateToMonthDay(activityPeriod?.to || "")}
                      </strong>{" "}
                      (UTC).
                    </BulletList.Item>
                    <BulletList.Item>
                      Higher-tier Keys access larger portions of the prize pool.
                    </BulletList.Item>
                  </BulletList>
                </BulletList.Item>
                <BulletList.Item>
                  <strong>Rewards:</strong> Each Key receives a reward; some are
                  Mythic
                  {!isLiveProgressKey &&
                    ` (up to ${prizeConfig.maxMythic} IMX)`}
                </BulletList.Item>
                <BulletList.Item>
                  <strong>Reward Claim:</strong> From{" "}
                  {isLiveProgressKey ? claimDate : "Wednesday"} (UTC); expires
                  in 30 days
                </BulletList.Item>
                <BulletList.Item>
                  <strong>Note:</strong> Gems are non-refundable
                </BulletList.Item>
              </BulletList>
            </Stack>

            <Stack gap="base.spacing.x1">
              <Heading size="small">Types of Rewards</Heading>
              <BulletList>
                <BulletList.Item>
                  {OverallPrizeTypes.join(", ")}
                </BulletList.Item>
              </BulletList>
            </Stack>

            <Stack gap="base.spacing.x1">
              <Heading size="small">T&Cs</Heading>
              <Body>
                <Link
                  href="https://www.immutable.com/legal/perpetual-rewards-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions apply
                </Link>
              </Body>
            </Stack>
          </Stack>
        </Box>
      </DialogContent>

      {showGetKeyButton && onRedeemTicket && (
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <RedeemKeyButton
              onCloseRulesModal={onClose}
              onRedeemTicket={onRedeemTicket}
              shouldDirectlyRedeem
              hasFreeEntry={hasFreeEntry}
              hasEnoughGems={hasEnoughGems}
              entryFee={gemsEntryAmount}
              hasTicketUpgrade={hasTicketUpgrade}
            />
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}
